import type { AudienceFilters } from '@brushfire/types'
import {
  PANEL_CHECKBOX_OPTIONS,
  US_STATES,
} from '@/features/instruments/publish/target-audience/lib/demographicFilterCatalog'
import type { CampaignInstrument } from '../types'

/**
 * Figma 62:1939 — instrument table columns.
 * Action uses `max-content` as its floor so dashboard / pause / stop / edit / remind
 * stay fully visible. The other tracks absorb the difference; the table does not scroll.
 */
export const LAUNCH_INSTRUMENT_GRID_COLUMNS =
  'minmax(0, 1.2fr) minmax(0, 1.35fr) minmax(0, 0.65fr) minmax(0, 0.75fr) minmax(0, 0.7fr) minmax(max-content, 0.85fr)'

export const REGULAR_SURVEY_LABEL = 'Regular Survey'
export const DYNAMIC_SURVEY_LABEL = 'Dynamic Survey'
/** BFP-1141 — published to every registered Web + Mobile user, no filters. */
export const ALL_REGISTERED_SURVEY_LABEL = 'All Registered Users'
export const ALL_ELIGIBLE_RESPONDERS_LABEL = 'All Eligible'
export const ALL_REGISTERED_RESPONDERS_LABEL = 'All Registered'
export const NOT_PUBLISHED_LABEL = 'Not Published'
export const NO_REWARD_LABEL = 'No Reward'

/** Figma 62:1974 — category chips, not "Label: value". */
const FILTER_LABELS: Record<string, string> = {
  ageRange: 'Age Range',
  gender: 'Gender',
  location: 'Location',
  education: 'Education Level',
  employment: 'Employment Status',
  deviceType: 'Device Type',
  maritalStatus: 'Martial Status',
  householdSize: 'House Hold Size',
  numberOfChildren: 'No. of Children',
  industry: 'Industry',
  housingStatus: 'Housing Status',
  registeredToVote: 'Currently Registered to Vote',
  currentVoterRegistration: 'Current Voter Registration',
  ekycStatus: 'eKYC',
  registeredPoliticalParty: 'Political Party',
}

export type LaunchAudienceTagTone = 'neutral' | 'district'

export interface LaunchAudienceTag {
  label: string
  tone: LaunchAudienceTagTone
}

const KNOWN_FILTER_ORDER = [
  'ageRange',
  'gender',
  'location',
  'senateDistrict',
  'education',
  'employment',
  'maritalStatus',
  'householdSize',
  'numberOfChildren',
  'industry',
  'housingStatus',
  'registeredToVote',
  'currentVoterRegistration',
  'deviceType',
  'ekycStatus',
  'registeredPoliticalParty',
] as const

function optionLabel(
  options: readonly { value: string; label: string }[],
  value: string,
): string {
  return options.find((option) => option.value === value)?.label ?? value
}

function joinLabels(
  options: readonly { value: string; label: string }[],
  values: string[],
): string | null {
  const labels = values.map((value) => optionLabel(options, value)).filter((label) => label.length > 0)
  return labels.length ? labels.join(', ') : null
}

function formatAge(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null
  const range = value as { min?: unknown; max?: unknown }
  const min = typeof range.min === 'number' ? range.min : null
  const max = typeof range.max === 'number' ? range.max : null
  if (min != null && max != null) return `${min}–${max}`
  if (min != null) return `${min}+`
  if (max != null) return `≤${max}`
  return null
}

function formatHousehold(values: string[]): string | null {
  const numeric = values.map(Number)
  const allNumeric = numeric.every((value) => Number.isFinite(value))
  if (allNumeric && numeric.length > 1) {
    const sorted = [...numeric].sort((a, b) => a - b)
    const consecutive = sorted.every((value, index) => index === 0 || value === sorted[index - 1] + 1)
    if (consecutive) return `${sorted[0]}–${sorted.at(-1)}`
  }
  return joinLabels(PANEL_CHECKBOX_OPTIONS.householdSize, values)
}

function formatDeviceLabel(value: string): string {
  if (value === 'mobile') return 'Mobile'
  if (value === 'web') return 'Web'
  return value
}

function formatEkycToken(value: string): string {
  if (value === 'verified') return 'Verified'
  if (value === 'unverified') return 'Unverified'
  return value
}

function formatEkycFilter(value: unknown): string | null {
  if (typeof value !== 'string') return null
  if (value === 'all' || !value.trim()) return null
  return formatEkycToken(value)
}

function formatStringList(key: string, values: string[]): string | null {
  if (key === 'householdSize') return formatHousehold(values)
  if (key === 'deviceType') {
    const labels = values.map(formatDeviceLabel)
    return labels.length ? labels.join(', ') : null
  }
  if (key === 'ekycStatus') {
    return values.map(formatEkycToken).join(', ')
  }
  const options = PANEL_CHECKBOX_OPTIONS[key as keyof typeof PANEL_CHECKBOX_OPTIONS]
  if (options) return joinLabels(options, values)
  return values.filter((item) => item.trim().length > 0).join(', ') || null
}

function formatLocation(value: unknown): string[] {
  if (!value || typeof value !== 'object') return []
  const location = value as { state?: unknown; city?: unknown }
  const tags: string[] = []
  if (typeof location.state === 'string' && location.state.trim()) {
    tags.push(`State: ${optionLabel(US_STATES, location.state.trim())}`)
  }
  if (typeof location.city === 'string' && location.city.trim()) {
    tags.push(`City: ${location.city.trim()}`)
  }
  return tags
}

function formatFilterValue(key: string, value: unknown): string | null {
  if (value == null) return null
  if (key === 'ageRange') return formatAge(value)
  if (key === 'senateDistrict' && typeof value === 'string' && value.trim()) return value.trim()
  if (key === 'ekycStatus') return formatEkycFilter(value)
  if (Array.isArray(value)) {
    const strings = value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    return strings.length ? formatStringList(key, strings) : null
  }
  if (typeof value === 'string' && value.trim()) return value.trim()
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>
    const parts = Object.entries(record)
      .filter(([, entry]) => entry != null && entry !== '')
      .map(([entryKey, entry]) => `${entryKey}: ${String(entry)}`)
    return parts.length ? parts.join(', ') : null
  }
  return null
}

function categoryLabel(key: string): string {
  return (
    FILTER_LABELS[key] ??
    key.replaceAll(/([A-Z])/g, ' $1').replace(/^./, (char) => char.toUpperCase())
  )
}

/** Category chips from saved publishing filters. Senate district shows the value in gold. */
export function buildLaunchAudienceTags(
  filters: AudienceFilters | Record<string, unknown> | null | undefined,
  dynamicSurvey: boolean,
  allRegistered = false,
): LaunchAudienceTag[] {
  if (allRegistered) return [{ label: ALL_REGISTERED_RESPONDERS_LABEL, tone: 'neutral' }]
  const source: Record<string, unknown> =
    filters && typeof filters === 'object' ? { ...filters } : {}
  const keys = [
    ...KNOWN_FILTER_ORDER.filter((key) => key in source),
    ...Object.keys(source).filter(
      (key) => !KNOWN_FILTER_ORDER.includes(key as (typeof KNOWN_FILTER_ORDER)[number]),
    ),
  ]
  const tags: LaunchAudienceTag[] = []
  for (const key of keys) {
    if (key === 'senateDistrict') {
      const district = formatFilterValue(key, source[key])
      if (district) tags.push({ label: district, tone: 'district' })
      continue
    }
    const formatted = key === 'location' ? formatLocation(source[key])[0] : formatFilterValue(key, source[key])
    if (formatted) tags.push({ label: categoryLabel(key), tone: 'neutral' })
  }
  if (tags.length === 0 && !dynamicSurvey) {
    return [{ label: ALL_ELIGIBLE_RESPONDERS_LABEL, tone: 'neutral' }]
  }
  return tags
}

export type LaunchSurveyTypeLabel =
  | typeof REGULAR_SURVEY_LABEL
  | typeof DYNAMIC_SURVEY_LABEL
  | typeof ALL_REGISTERED_SURVEY_LABEL

export function launchSurveyTypeLabel(
  audienceAvailability: CampaignInstrument['audienceAvailability'],
): LaunchSurveyTypeLabel {
  if (audienceAvailability === 'existing_and_future') return DYNAMIC_SURVEY_LABEL
  if (audienceAvailability === 'all_registered') return ALL_REGISTERED_SURVEY_LABEL
  return REGULAR_SURVEY_LABEL
}

export function formatLaunchPublishedDate(publishedAt: string | null | undefined): string {
  if (!publishedAt) return NOT_PUBLISHED_LABEL
  const date = new Date(publishedAt)
  if (Number.isNaN(date.getTime())) return NOT_PUBLISHED_LABEL
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}

export function formatLaunchReward(rewardPerResponse: number | null | undefined): string {
  if (rewardPerResponse == null || !Number.isFinite(rewardPerResponse) || rewardPerResponse <= 0) {
    return NO_REWARD_LABEL
  }
  return `$${rewardPerResponse.toFixed(2)}`
}
